import { useEffect, useRef } from 'react';

import { uuid } from '@atlaskit/adf-schema/uuid';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { AgentAttribution, AgentInfo } from '@atlassian/editor-plugin-ai/types';

import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
	StreamingError,
} from './aiStreamingOrchestratorPluginType';
import {
	fireADFStreamingEndSessionErrorAnalytics,
	fireResumeAbortedToolCallFallbackAnalytics,
} from './editor-actions/analytics';
import { createStreamFlowPacer } from './editor-actions/stream-flow-pacer';
import { StreamCommandQueue } from './editor-actions/streamCommandQueue';

export const aiStreamingOrchestratorPlugin: AiStreamingOrchestratorPlugin = ({ api, config }) => {
	if (!api) {
		throw new Error('api is not defined');
	}

	let streamQueue: StreamCommandQueue | undefined;

	// Only allow one session to be active at a time
	let sessionId: string | null = null;
	// Tracking variables for debugging session lifecycle issues
	let hasSessionBeenStarted = false;
	let isEndingSession = false;
	let lastEndedSessionId: string | null = null;
	let endSessionCallCount = 0;
	let sessionAborted = false;
	// Replaced per session: an aborted queue's request keeps running and pushing into
	// its pacer, which would otherwise emit into the next session.
	let streamFlowPacer = createStreamFlowPacer(config?.streamFlowPacerEnabled);
	const telemetryEventsEnabled = config?.telemetryEventsEnabled === true;

	const commitQueuedToolCall = (
		command: StreamingCommand,
		value: string,
		options?: {
			abortedToolCallId?: string;
			hideLoadingScreen?: boolean;
			resume?: { toolCallId: string };
		},
	) => {
		if (sessionId === null) {
			throw new Error('commitToolCall requires a session to be started first!');
		}
		const actionId = crypto.randomUUID();
		streamQueue?.initStreamCommand(actionId, command, {
			applyMode: 'commit',
			hideLoadingScreen: options?.hideLoadingScreen,
			resume: options?.resume,
		});
		streamQueue?.queueStreamCommand(actionId, value);
		streamQueue?.endStreamCommand(actionId, {
			forceAbortedToolCallId: options?.abortedToolCallId,
		});
		return actionId;
	};

	return {
		name: 'aiStreamingOrchestrator',
		actions: {
			startSession: (
				traceId?: string,
				params?: {
					agentAttribution?: AgentAttribution;
					agentInfo?: AgentInfo;
					/** True for a create stream; suppresses Post Stream Review. */
					isCreateSession?: boolean;
					/**
					 * True for an inline-edit stream; gates the Post Stream
					 * Review auto-open on the orchestrated (Rovo-chat) update path.
					 */
					isInlineEditSession?: boolean;
					/**
					 * True when the originating edit was from Rovo chat (`chat_edit` or
					 * Create-with-Rovo).
					 */
					isChatEditSession?: boolean;
					promptVersion?: string;
					singleInstrumentationId?: string;
				},
			) => {
				sessionId = crypto.randomUUID() ?? uuid.generate();
				hasSessionBeenStarted = true;
				sessionAborted = false;
				if (expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true)) {
					streamFlowPacer?.destroy();
					streamFlowPacer = createStreamFlowPacer(config?.streamFlowPacerEnabled);
				}
				streamQueue = new StreamCommandQueue(api, streamFlowPacer, telemetryEventsEnabled);
				streamQueue.startSession(
					sessionId,
					traceId,
					params?.singleInstrumentationId,
					fg('platform_editor_ai_promptversion_in_analytics') ? params?.promptVersion : undefined,
				);
				// agentInfo is already gated by the platform_editor_agent_mentions experiment
				// in update-command-processor — it will be undefined when the experiment is off,
				// so no additional gate is needed here.
				api.aiExperience.actions.startSession(
					sessionId,
					traceId,
					fg('platform_editor_ai_promptversion_in_analytics') ? params?.promptVersion : undefined,
					params?.agentInfo,
					params?.isCreateSession,
					params?.isInlineEditSession,
					params?.agentAttribution,
					params?.isChatEditSession,
				);
				return sessionId;
			},

			endSession: async (error?: StreamingError) => {
				// In the session abort we end the session and no longer need to run this
				// On a new session we reset the state so this is safe to ignore
				if (sessionAborted) {
					return;
				}
				if (sessionId === null) {
					fireADFStreamingEndSessionErrorAnalytics({
						api,
						sessionStarted: hasSessionBeenStarted,
						endSessionCallCount,
						lastEndedSessionId,
						isDoubleCall: isEndingSession,
					});
					throw new Error(`endSession requires a session to be started first!`);
				}

				endSessionCallCount++;
				isEndingSession = true;
				const currentSessionId = sessionId;

				try {
					streamQueue?.queueEndSession(currentSessionId);
					if (error) {
						if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
							api.aiExperience.actions.dispatchAIEvent({
								type: 'error',
								errorInfo: {
									failureReason: error.failureReason,
									statusCode: error.statusCode,
								},
							});
						} else {
							const { send } = api.aiExperience.actions.getAiExperienceActor();
							send({
								type: 'error',
								errorInfo: {
									failureReason: error.failureReason,
									statusCode: error.statusCode,
								},
							});
						}
					}
					// Since `endSession` is going to be called after all the actions are invoked
					// so we can basically wait for the queue to be empty
					await streamQueue?.onEmptyQueue();
					streamQueue?.endSession(currentSessionId);
					// https://atlassian.slack.com/archives/C0962DYFLRL/p1759397599541949?thread_ts=1759366160.706849&cid=C0962DYFLRL
					// streamQueue?.destroy();
					api.aiExperience.actions.endSession(currentSessionId, {
						type: error ? 'error' : 'finished',
					});
					hasSessionBeenStarted = false;
					lastEndedSessionId = currentSessionId;
					sessionId = null;
				} finally {
					isEndingSession = false;
				}
			},

			abortSession: () => {
				// abortSession can be called multiple times from multiple command handlers
				if (!sessionId) {
					return;
				}

				const sessionToEnd = sessionId;
				lastEndedSessionId = sessionToEnd;
				sessionId = null;
				streamQueue?.abort();
				if (sessionToEnd) {
					api.aiExperience.actions.endSession(sessionToEnd, { type: 'abort' });
					sessionAborted = true;
				}
			},

			initAction: (command, options) => {
				if (sessionId === null) {
					throw new Error('initAction requires a session to be started first!');
				}
				const actionId = crypto.randomUUID();
				streamQueue?.initStreamCommand(actionId, command, options);
				return actionId;
			},

			// One-shot write: apply (or resume at a stored range), emit the value,
			// and finish. Commit mode skips the typewriter pacer.
			commitToolCall: commitQueuedToolCall,

			// Resume a tool call aborted by the queue timeout, re-applying its content
			// at the stored (mapped) position. Same commit write as a finished tool call.
			resumeAction: (toolCallId, command, fullValue) => {
				if (sessionId === null) {
					throw new Error('resumeAction requires a session to be started first!');
				}
				const stored = api.aiExperience.actions.getAbortedToolCallPosition?.(toolCallId);

				if (!stored) {
					// Should not happen in practice (content is already written before a
					// timeout abort). Fire an anomaly so this degradation is observable in
					// production. The queue's resume path will itself fall back to
					// re-running the real command when no stored position is found.
					fireResumeAbortedToolCallFallbackAnalytics({ api, commandType: command.type });
				}

				// Resume mode is keyed solely by `toolCallId`: the queue resolves the
				// LATEST (transaction-remapped) stored position at apply time. This
				// matters when multiple aborted tool calls are resumed in sequence —
				// each earlier resume mutates the document and shifts the not-yet-resumed
				// positions, so reading the up-to-date value at apply time keeps every
				// resume anchored to its own content instead of collapsing onto a stale,
				// shared range.
				commitQueuedToolCall(command, fullValue, { resume: { toolCallId } });
			},

			// We are sending the full document as a chunk for create actions
			streamValue: (actionId: string, chunk: string) => {
				// Use the queue manager to handle streaming
				// This will either process immediately if actor is ready, or queue for later
				streamQueue?.queueStreamCommand(actionId, chunk);
			},

			// HTML+ v2 incremental tool-call streaming contract:
			// Each chunk is a full snapshot of the value, not a delta.
			//
			// IMPORTANT — REPLACE-NOT-APPEND INVARIANT:
			// At the queue layer this is currently equivalent to `streamValue`
			// because `streamCommandQueue.ts#processStreamCommand` resets the
			// internal ADFStreamer accumulator on every processed chunk before
			// parsing. **Two distinct public actions (`streamValue` and
			// `replaceValue`) depend on this single internal behaviour.** If
			// `streamValue` is ever migrated to append semantics for the v1
			// path, this `replaceValue` body MUST be rewired to a dedicated
			// `queueReplaceCommand` (or equivalent) on `StreamCommandQueue`
			// that preserves the replace-on-snapshot guarantee, otherwise v2
			// callers (`ToolCallProcessorV2`) will silently break — each
			// snapshot would be appended to the previous one instead of
			// replacing it, producing duplicated / concatenated ADF.
			//
			// See `streamCommandQueue.ts#processStreamCommand` for the
			// implementation detail this invariant relies on.
			replaceValue: (actionId: string, fullValue: string) => {
				streamQueue?.queueStreamCommand(actionId, fullValue);
			},

			notifySchemaContentRejection: (actionId: string, errorMessage?: string) => {
				streamQueue?.notifySchemaContentRejection(actionId, errorMessage);
			},

			finishAction: (
				actionId: string,
				error?: StreamingError,
				options?: { abortedToolCallId?: string },
			) => {
				if (error) {
					if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
						if (
							error.errorKey !== 'user_abort' ||
							!expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true)
						) {
							api.aiExperience.actions.dispatchAIEvent({
								type: 'error',
								errorInfo: {
									failureReason: error.failureReason,
									statusCode: error.statusCode,
								},
							});
						}
					} else {
						if (
							error.errorKey !== 'user_abort' ||
							!expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true)
						) {
							const { send } = api.aiExperience.actions.getAiExperienceActor();
							send({
								type: 'error',
								errorInfo: {
									failureReason: error.failureReason,
									statusCode: error.statusCode,
								},
							});
						}
					}
				}
				// On a timeout force-finish, pass the aborted tool call id through
				// endStreamCommand. It rides the terminal `complete` event so
				// `buildCommitTransaction` (the last write of the tool call's content)
				// persists its final positions for resume.
				streamQueue?.endStreamCommand(actionId, {
					forceAbortedToolCallId: options?.abortedToolCallId,
				});
			},
		},
		usePluginHook: () => {
			const prevStreamPausedRef = useRef<boolean | undefined>(undefined);
			usePluginStateEffect(
				api,
				['aiExperience'],
				({ aiExperienceState }) => {
					const streamPaused = aiExperienceState?.aiSessionPluginState?.streamPaused;
					if (streamPaused !== prevStreamPausedRef.current) {
						if (streamPaused) {
							streamFlowPacer?.pause();
						} else {
							streamFlowPacer?.resume();
						}
						prevStreamPausedRef.current = streamPaused;
					}
				},
				{ disabled: !expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true) },
			);

			useEffect(() => {
				return () => {
					streamQueue?.destroy();
					streamFlowPacer?.destroy();
				};
			}, []);
		},
	};
};
